import { FC } from 'react';
import { useHistory, useLocation } from 'react-router';
import { useDisclosure } from '@chakra-ui/react';

import { SensitiveOperationType } from '__generated__/GQL';
import { useCurrentPricingPlan } from 'common/hooks/useCurrentPricingPlan';
import Callout from 'components/_core/Callout';
import Heading from 'components/_core/Heading';
import SunshineCard from 'components/_core/SunshineCard';
import Text from 'components/_core/Text';
import BackButton from 'components/BackButton';
import SMSVerificationModal from 'components/SMSVerificationModal';
import { StepProps } from 'components/Stepper/types';
import useToast from 'components/ToastProvider/useToast';
import { VAT_RATE } from 'helpers/amount';
import { formatDate } from 'helpers/date';
import * as Layout from 'layouts/StepperLayout';

import * as routes from '../../../../../routes';
import { CtaButton } from '../../../components/Buttons';
import { useOrderVirtualPaymentCard } from '../../hooks/useOrderVirtualPaymentCard';
import { useVirtualCardOrderFlowContext } from '../../hooks/useVirtualCardOrderFlowContext';
import locales from './locales';
import { ErrorBoxWrapper, InfoBoxWrapper, VatMentionText } from './styles';
import { getQuotaWarningLocale } from './utils';

const VirtualCardOrderRecap: FC<StepProps> = ({ currentStepIdx, goBack }) => {
  const { goBack: goBackHistory, replace } = useHistory();
  const { state } = useLocation<{ fromFirstVirtualCardSummary: boolean }>();
  const isFromFirstVirtualCardSummary = !!state?.fromFirstVirtualCardSummary;
  const { showToast } = useToast();

  const {
    error: cardOrderingError,
    loading: cardOrderingLoading,
    orderVirtualPaymentCard,
  } = useOrderVirtualPaymentCard();

  const { flowConfig, sharedState } = useVirtualCardOrderFlowContext();

  const { alias, deactivateAt } = sharedState;
  const {
    bankAccount,
    formattedPlanName,
    freeCardsIncluded,
    freeCardsLeft,
    priceAfterQuotaReachedWithoutVAT,
    priceWithoutVAT,
  } = flowConfig;

  const { plan } = useCurrentPricingPlan();
  const hasQuotaLimit = !plan?.limits.virtualPaymentCardMonth.isUnlimited;

  const cardOrderTotalPriceWithVat = priceWithoutVAT * VAT_RATE;
  const isFreeCard = cardOrderTotalPriceWithVat === 0;

  const hasEnoughFunds = bankAccount.nextBalance >= cardOrderTotalPriceWithVat;

  const { isOpen, onClose, onToggle } = useDisclosure({
    defaultIsOpen: false,
  });

  const onVerificationSuccess = async (token: string) => {
    const { creditCard } = await orderVirtualPaymentCard({
      input: {
        alias,
        bankAccountId: bankAccount.bankAccountId,
        deactivateAt,
      },
      token,
    });

    if (creditCard) {
      showToast({ message: locales.creationSuccessMessage, type: 'success' });
      replace(`/cards/${creditCard.creditCardId}`);
    }
  };

  const onNextPressed = () => {
    onToggle();
  };

  const handleBackButtonClick = () => {
    if (isFromFirstVirtualCardSummary) {
      replace(routes.root);
    } else if (currentStepIdx === 0) {
      goBackHistory();
    }
    goBack();
  };

  return (
    <>
      <SMSVerificationModal
        actionData={{}}
        actionType={SensitiveOperationType.CreateVirtualCard}
        isOpen={isOpen}
        onClose={onClose}
        onSuccess={onVerificationSuccess}
      />
      <Layout.Root>
        <Layout.Body>
          <Layout.HeaderButtonsContainer>
            <BackButton
              isDisabled={cardOrderingLoading}
              onClick={handleBackButtonClick}
            />
          </Layout.HeaderButtonsContainer>

          <Layout.BodyScrollableSection>
            <Heading marginBottom="space-16" size="lg">
              {locales.title}
            </Heading>

            <Text marginBottom="space-24">{locales.description}</Text>

            <SunshineCard.Group>
              <SunshineCard alignItems="center">
                <SunshineCard.Content>
                  <SunshineCard.Text size="md">
                    {locales.price}
                  </SunshineCard.Text>
                </SunshineCard.Content>

                <SunshineCard.Slot name="action">
                  <SunshineCard.Text size="md" variant="primary">
                    {isFreeCard
                      ? locales.priceFree
                      : locales.formatString(
                          locales.pricePerMonth,
                          priceWithoutVAT,
                        )}
                  </SunshineCard.Text>
                </SunshineCard.Slot>
              </SunshineCard>

              <SunshineCard alignItems="center">
                <SunshineCard.Content>
                  <SunshineCard.Text size="md">
                    {locales.deactivation}
                  </SunshineCard.Text>
                </SunshineCard.Content>

                <SunshineCard.Slot name="action">
                  <SunshineCard.Text size="md" variant="primary">
                    {deactivateAt
                      ? formatDate(new Date(deactivateAt), 'PPP')
                      : locales.unspecifiedDeactivationDate}
                  </SunshineCard.Text>
                </SunshineCard.Slot>
              </SunshineCard>
            </SunshineCard.Group>

            {cardOrderTotalPriceWithVat > 0 ? (
              <VatMentionText>{locales.vatMention}</VatMentionText>
            ) : null}

            {hasQuotaLimit ? (
              <InfoBoxWrapper>
                <Callout hideIcon variant="info">
                  <Callout.Content>
                    {
                      locales.formatString(
                        getQuotaWarningLocale(freeCardsIncluded, freeCardsLeft),
                        {
                          cardsLeft: freeCardsLeft,
                          plan: formattedPlanName,
                          price: priceAfterQuotaReachedWithoutVAT,
                        },
                      ) as string
                    }
                  </Callout.Content>
                </Callout>
              </InfoBoxWrapper>
            ) : null}

            {hasEnoughFunds ? null : (
              <ErrorBoxWrapper>
                <Callout hideIcon variant="error">
                  <Callout.Content>
                    {locales.errorBoxNotEnoughFunds}
                  </Callout.Content>
                </Callout>
              </ErrorBoxWrapper>
            )}

            {cardOrderingError ? (
              <ErrorBoxWrapper>
                <Callout hideIcon variant="error">
                  <Callout.Content>{locales.orderErrorMessage}</Callout.Content>
                </Callout>
              </ErrorBoxWrapper>
            ) : null}
          </Layout.BodyScrollableSection>

          <Layout.FooterContainer>
            <Layout.FooterButtonsContainer>
              <CtaButton
                isDisabled={!hasEnoughFunds}
                isLoading={cardOrderingLoading}
                onClick={onNextPressed}
              >
                {isFreeCard ? locales.freeOrderCta : locales.paidOrderCta}
              </CtaButton>
            </Layout.FooterButtonsContainer>
          </Layout.FooterContainer>
        </Layout.Body>
      </Layout.Root>
    </>
  );
};

export default VirtualCardOrderRecap;
